import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-950">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/login">
          <Button variant="ghost" size="sm" className="gap-2 mb-8 rounded-sm">
            <ArrowLeft size={16} /> Back
          </Button>
        </Link>

        <h1 className="text-3xl font-semibold text-slate-900 dark:text-white mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Terms of Service
        </h1>
        <p className="text-sm text-slate-500 mb-8">Last updated: March 11, 2026</p>

        <div className="prose prose-slate dark:prose-invert max-w-none text-sm leading-relaxed space-y-6" data-testid="terms-of-service-content">
          <section>
            <h2 className="text-lg font-semibold mt-8 mb-3">1. Acceptance of Terms</h2>
            <p>By accessing or using the Contrack Contract Lifecycle Management platform ("Platform") operated by ProcureAide Ltd ("Company", "we", "us"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree, do not use the Platform.</p>
            <p>These Terms constitute a legally binding agreement between you and ProcureAide Ltd. If you are using the Platform on behalf of an organization, you represent that you have authority to bind that organization to these Terms.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mt-8 mb-3">2. Description of Service</h2>
            <p>Contrack is a cloud-based contract lifecycle management platform that provides:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Contract drafting, editing, and version control</li>
              <li>Template and clause library management</li>
              <li>Configurable approval workflows</li>
              <li>AI-assisted contract analysis (risk review, obligation extraction, deliverable identification)</li>
              <li>Compliance monitoring and obligation tracking</li>
              <li>Reporting and analytics dashboards</li>
              <li>Role-based access control and user management</li>
              <li>Email notifications and automated reminders</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold mt-8 mb-3">3. User Accounts</h2>
            <p><strong>3.1 Registration:</strong> You must provide accurate and complete information when creating an account. You are responsible for maintaining the confidentiality of your login credentials.</p>
            <p><strong>3.2 Account Security:</strong> You must immediately notify us of any unauthorized use of your account. We implement security measures including password hashing, account lockout policies, and session management, but you are responsible for safeguarding your credentials.</p>
            <p><strong>3.3 Account Termination:</strong> We may suspend or terminate your account if you violate these Terms or engage in harmful activities. You may delete your account at any time via the account settings, which will trigger anonymization of your personal data in accordance with our Privacy Policy.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mt-8 mb-3">4. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Use the Platform for any unlawful purpose or in violation of any applicable laws</li>
              <li>Upload malicious code, viruses, or harmful content</li>
              <li>Attempt to gain unauthorized access to other users' accounts or data</li>
              <li>Circumvent or disable security features of the Platform</li>
              <li>Use automated tools to scrape, mine, or extract data from the Platform</li>
              <li>Upload contracts or data that you do not have the right to process</li>
              <li>Interfere with or disrupt the integrity or performance of the Platform</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold mt-8 mb-3">5. Intellectual Property</h2>
            <p><strong>5.1 Platform IP:</strong> The Platform, including its software, design, features, and documentation, is owned by ProcureAide Ltd and protected by intellectual property laws. You are granted a limited, non-exclusive, non-transferable license to use the Platform for its intended purpose.</p>
            <p><strong>5.2 Your Content:</strong> You retain ownership of all contracts, templates, clauses, and other content you upload or create on the Platform. By using the Platform, you grant us a limited license to process, store, and display your content solely for the purpose of providing the service.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mt-8 mb-3">6. AI-Powered Features</h2>
            <p><strong>6.1</strong> The Platform uses artificial intelligence (OpenAI GPT-4o) to assist with contract analysis, including risk review, obligation extraction, and deliverable identification.</p>
            <p><strong>6.2</strong> AI-generated outputs are provided as suggestions and should not be considered legal advice. You are solely responsible for reviewing and validating all AI outputs before relying on them.</p>
            <p><strong>6.3</strong> Contract content sent to AI services is transmitted securely and is not used to train third-party AI models.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mt-8 mb-3">7. Data Protection</h2>
            <p>Your use of the Platform is also governed by our <Link to="/privacy" className="text-cyan-600 underline">Privacy Policy</Link>, which describes how we collect, use, and protect your personal data in compliance with the GDPR and applicable data protection laws.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mt-8 mb-3">8. Service Availability</h2>
            <p><strong>8.1</strong> We aim to maintain high availability but do not guarantee uninterrupted access to the Platform. Scheduled maintenance will be communicated in advance where possible.</p>
            <p><strong>8.2</strong> We may modify, suspend, or discontinue features of the Platform at any time with reasonable notice.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mt-8 mb-3">9. Limitation of Liability</h2>
            <p><strong>9.1</strong> To the maximum extent permitted by law, ProcureAide Ltd shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Platform.</p>
            <p><strong>9.2</strong> Our total liability for any claim arising from or related to these Terms shall not exceed the amount paid by you to us in the 12 months preceding the claim.</p>
            <p><strong>9.3</strong> The Platform is not a substitute for professional legal advice. We are not responsible for decisions made based on contract analysis or AI outputs provided by the Platform.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mt-8 mb-3">10. Indemnification</h2>
            <p>You agree to indemnify and hold harmless ProcureAide Ltd, its officers, directors, employees, and agents from any claims, damages, losses, or expenses arising from your violation of these Terms or misuse of the Platform.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mt-8 mb-3">11. Governing Law and Disputes</h2>
            <p>These Terms are governed by the laws of England and Wales. Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the courts of England and Wales.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mt-8 mb-3">12. Changes to Terms</h2>
            <p>We reserve the right to modify these Terms at any time. Material changes will be communicated via email or a prominent notice on the Platform at least 30 days before taking effect. Your continued use of the Platform after changes constitutes acceptance.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mt-8 mb-3">13. Contact</h2>
            <p>For questions about these Terms, contact us at:</p>
            <ul className="list-none space-y-1">
              <li>Email: <strong>legal@procureaide.com</strong></li>
              <li>ProcureAide Ltd, London, United Kingdom</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}
